import React from "react";
import {Controller, useFormContext} from "react-hook-form";
import {Box, Colors, Container, Text} from "@bounce/shared/ui";
import {MenuItem, Select, TextField} from "../../../ui/components";
import {FieldLabel, FormCard, INPUT_SX} from "./FormCard";
import {PROMO_STATUSES, STATUS_DISPLAY} from "./constants";
import {toPromoKey} from "./helpers";
import {PromoFormValues} from "./schema";

const RANK_HINT =
  "Rank is the promo's place on the board. New promos are added last; drag rows on the board to reorder.";

const DetailsCard = () => {
  const {control, getValues, setValue, formState} = useFormContext<PromoFormValues>();
  const isNewPromo = !formState.defaultValues?.key;

  const syncKeyWithName = (previousName: string, nextName: string) => {
    if (!isNewPromo || getValues("key") !== toPromoKey(previousName)) return;
    setValue("key", toPromoKey(nextName), {shouldDirty: true, shouldValidate: formState.isSubmitted});
  };

  return (
    <FormCard title="Details">
      <Container row fullWidth sx={{gap: 2, alignItems: "flex-start", flexWrap: "nowrap"}}>
        <Box sx={{flex: 1}}>
          <FieldLabel required>Name</FieldLabel>
          <Controller
            name="name"
            control={control}
            render={({field: {ref, onChange, ...field}, fieldState}) => (
              <TextField
                {...field}
                inputRef={ref}
                onChange={(event) => {
                  syncKeyWithName(field.value, event.target.value);
                  onChange(event);
                }}
                fullWidth
                size="small"
                placeholder="Promo name"
                error={!!fieldState.error}
                sx={INPUT_SX}
              />
            )}
          />
        </Box>
        <Box sx={{flex: 1}}>
          <FieldLabel required>Key</FieldLabel>
          <Controller
            name="key"
            control={control}
            render={({field: {ref, ...field}, fieldState}) => (
              <TextField
                {...field}
                inputRef={ref}
                fullWidth
                size="small"
                placeholder="e.g. thanksgiving"
                error={!!fieldState.error}
                sx={INPUT_SX}
              />
            )}
          />
        </Box>
      </Container>
      <Container row fullWidth sx={{gap: 2}}>
        <Box sx={{flex: 1}}>
          <FieldLabel>Description</FieldLabel>
          <Controller
            name="description"
            control={control}
            render={({field: {ref, ...field}}) => (
              <TextField
                {...field}
                inputRef={ref}
                fullWidth
                size="small"
                placeholder="Optional. Shown in the promo list."
                sx={INPUT_SX}
              />
            )}
          />
        </Box>
      </Container>
      <Container row fullWidth sx={{gap: 2, alignItems: "flex-end", flexWrap: "nowrap"}}>
        <Box sx={{flex: 1}}>
          <FieldLabel required>Status</FieldLabel>
          <Controller
            name="status"
            control={control}
            render={({field}) => (
              <Select {...field} fullWidth size="small" sx={INPUT_SX}>
                {PROMO_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {STATUS_DISPLAY[status].label}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </Box>
        <Text size={12} sx={{flex: 1, color: Colors.grey._600, pb: 1}}>
          {RANK_HINT}
        </Text>
      </Container>
    </FormCard>
  );
};

export default DetailsCard;
